import { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import {
  ListChecks, Clock, Activity, CheckSquare,
  AlertCircle, AlertTriangle, Building2,
  CalendarDays, RefreshCw, ChevronDown,
  Eye, Edit3, Paperclip, FilePlus2, Send, X, Loader2, Trash2, Archive,
  Flag, Calculator, FileText, CheckCheck, Ruler
} from "lucide-react";
import axios from "axios";
import { API } from "../contexts/AuthContext";
import { Skeleton } from "../components/ui/skeleton";
import { toast } from "sonner";
import { Modal } from "./budgets/SharedComponents";
import { formatDateShort, daysUntil } from "../utils/format";
import { BudgetDetail } from "./budgets/BudgetDetail";
import { BudgetForm } from "./budgets/BudgetForm";
import { AttachmentsModal } from "./budgets/AttachmentsModal";
import { ComputoPapeleraModal } from "./ingenieria/ComputoPapeleraModal";
// Fix CL (2026-09-13): planos DXF → cómputo métrico de la tarea.
import PlanosComputoModal from "./ingenieria/PlanosComputoModal";
// Fix CM (2026-09-13): panel de tareas del flujo de trabajo.
import TareasPanel from "../components/TareasPanel";

const opts = { withCredentials: true };

// Textos por módulo. "computo" → Cómputo (De Obra/Varios); "proyecto" → Proyectos (De Proyecto).
const TIPO_META = {
  computo: {
    title: "Cómputo",
    subtitle: "Centro de control de elaboración de cómputos — tareas asignadas al Depto. de Ingeniería",
    upcomingTitle: "Próximos cómputos a vencer",
    papeleraTitle: "Papelera de Cómputos",
    emptyTitle: "No hay tareas de elaboración de cómputos asignadas",
    singular: "cómputo",
  },
  proyecto: {
    title: "Proyectos",
    subtitle: "Centro de control de elaboración de proyectos — tareas asignadas al Depto. de Ingeniería",
    upcomingTitle: "Próximos proyectos a vencer",
    papeleraTitle: "Papelera de Proyectos",
    emptyTitle: "No hay tareas de elaboración de proyectos asignadas",
    singular: "proyecto",
  },
};

const TASK_STATUS_META = {
  pendiente:  { label: "Pendiente",  color: "bg-amber-100 text-amber-700",   icon: Clock },
  en_proceso: { label: "En Proceso", color: "bg-blue-100 text-blue-700",     icon: Activity },
  completado: { label: "Completado", color: "bg-emerald-100 text-emerald-700", icon: CheckSquare },
};

const TERMINADO_META = { label: "Terminado", color: "bg-violet-100 text-violet-700 border-violet-200", icon: CheckCheck };

// daysUntil vive en utils/format.js (BLOQUE 17 — helper único sin desfase de huso horario).
// ─── Task Status Dropdown ──────────────────────────────────────────────────
function TaskStatusSelect({ task, onUpdate }) {
  const [open, setOpen] = useState(false);
  const meta = TASK_STATUS_META[task.status] || TASK_STATUS_META.pendiente;
  const Icon = meta.icon;

  const handleSelect = async (newStatus) => {
    setOpen(false);
    try {
      await axios.put(`${API}/computo-tasks/${task._id}`, { status: newStatus }, opts);
      onUpdate();
    } catch { toast.error("Error al actualizar estado"); }
  };

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold border transition-colors ${meta.color}`}
        data-testid={`task-status-btn-${task._id}`}
      >
        <Icon size={11} /> {meta.label} <ChevronDown size={10} />
      </button>
      {open && createPortal(
        <div className="fixed inset-0 z-[60]" onClick={() => setOpen(false)}>
          <div className="absolute bg-white border border-gray-200 rounded-xl shadow-xl py-1 min-w-[160px] z-[61]" onClick={e => e.stopPropagation()}>
            {Object.entries(TASK_STATUS_META).map(([key, m]) => {
              const MIcon = m.icon;
              return (
                <button key={key} onClick={() => handleSelect(key)}
                  className={`flex items-center gap-2 px-3 py-2 w-full text-left text-xs font-medium hover:bg-gray-50 ${task.status === key ? "bg-gray-50 font-semibold" : ""}`}
                >
                  <MIcon size={12} className={m.color.split(" ")[1]} /> {m.label}
                </button>
              );
            })}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

// ─── Edit Task Modal (deadline + alcance + observaciones) ──────────────────
function EditTaskModal({ open, onClose, task, onSaved }) {
  const [deadline, setDeadline] = useState("");
  const [alcance, setAlcance] = useState("");
  const [observaciones, setObservaciones] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && task) {
      setDeadline(task.deadline_computo || "");
      setAlcance(task.alcance || "");
      setObservaciones(task.observaciones || "");
    }
  }, [open, task]);

  if (!open || !task) return null;

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/computo-tasks/${task._id}`, {
        deadline_computo: deadline || null,
        observaciones: observaciones,
      }, opts);
      toast.success("Tarea actualizada");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="edit-task-modal">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <Edit3 size={18} className="text-[#2E6DA4]" />
            <h3 className="text-base font-bold text-gray-900">Editar Tarea de Cómputo</h3>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100"><X size={16} className="text-gray-500" /></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="bg-gray-50 rounded-lg px-3 py-2 border border-gray-100">
            <p className="text-xs text-gray-500">Presupuesto</p>
            <p className="text-sm font-bold text-gray-900">{task.budget_nombre}</p>
            <p className="text-xs text-gray-500 mt-0.5">{task.budget_number}</p>
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1.5">Fecha Límite de Entrega</label>
            <input
              type="date"
              value={deadline}
              onChange={e => setDeadline(e.target.value)}
              className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#2E6DA4]/30 focus:border-[#2E6DA4]"
              data-testid="edit-task-deadline"
            />
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1.5">Alcance <span className="text-gray-400 font-normal">(solo lectura — viene del presupuesto)</span></label>
            <textarea
              value={alcance}
              readOnly
              rows={3}
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm bg-gray-50 text-gray-600 resize-none"
            />
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1.5">Observaciones</label>
            <textarea
              value={observaciones}
              onChange={e => setObservaciones(e.target.value)}
              rows={3}
              placeholder="Notas internas del Dpto. de Proyectos..."
              className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-[#2E6DA4]/30 resize-none"
              data-testid="edit-task-observaciones"
            />
          </div>
        </div>
        <div className="px-6 pb-6 flex gap-3">
          <button onClick={onClose} className="flex-1 border border-gray-300 text-gray-700 rounded-xl py-2.5 text-sm font-medium hover:bg-gray-50">Cancelar</button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 flex items-center justify-center gap-2 bg-[#2E6DA4] hover:bg-[#245A8A] text-white rounded-xl py-2.5 text-sm font-semibold transition-colors disabled:opacity-60"
            data-testid="edit-task-save-btn"
          >
            {saving ? <Loader2 size={15} className="animate-spin" /> : <Edit3 size={15} />}
            {saving ? "Guardando..." : "Guardar"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ─── MAIN PAGE (parametrizado por tipo: "computo" | "proyecto") ─────────────
export function ComputoTasksPage({ tipo = "computo" }) {
  const meta = TIPO_META[tipo] || TIPO_META.computo;
  const isProyectoModule = tipo === "proyecto";
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("todos");

  // Modal state
  const [viewBudget, setViewBudget] = useState(null);
  const [editTask, setEditTask] = useState(null);
  const [attachBudget, setAttachBudget] = useState(null);
  const [formBudget, setFormBudget] = useState(null);
  const [enviandoTaskId, setEnviandoTaskId] = useState(null);
  const [deleteTask, setDeleteTask] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showPapelera, setShowPapelera] = useState(false);
  const [planosTask, setPlanosTask] = useState(null);   // Fix CL (2026-09-13)
  // PASO C — terminar proyecto (solo módulo Proyecto)
  const [terminarTask, setTerminarTask] = useState(null);
  const [terminarDestinos, setTerminarDestinos] = useState({ computo: false, presupuesto: false });
  const [terminando, setTerminando] = useState(false);

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    try {
      const [tRes, pRes, cRes] = await Promise.all([
        axios.get(`${API}/computo-tasks?tipo=${tipo}`, opts),
        axios.get(`${API}/projects`, opts).catch(() => ({ data: [] })),
        axios.get(`${API}/listas/clientes`, opts).catch(() => ({ data: [] })),
      ]);
      setTasks(Array.isArray(tRes.data) ? tRes.data : []);
      setProjects(pRes.data || []);
      setClientes(cRes.data || []);
    } catch { toast.error("Error al cargar tareas"); }
    finally { setLoading(false); }
  }, [tipo]);

  useEffect(() => { fetchTasks(); }, [fetchTasks]);

  const filtered = filterStatus === "todos" ? tasks : tasks.filter(t => t.status === filterStatus);

  const stats = {
    pendiente: tasks.filter(t => t.status === "pendiente").length,
    en_proceso: tasks.filter(t => t.status === "en_proceso").length,
    completado: tasks.filter(t => t.status === "completado").length,
  };

  // Top 5 próximos a vencer (mismo modelo visual que "Próximos presupuestos a vencer")
  const upcomingTasks = (() => {
    const candidatas = tasks.filter(t => t.status !== "completado" && t.deadline_computo);
    candidatas.sort((a, b) => (a.deadline_computo || "").localeCompare(b.deadline_computo || ""));
    return candidatas.slice(0, 5);
  })();

  // Helper: fetch budget by ID
  const fetchBudget = async (budgetId) => {
    try {
      const res = await axios.get(`${API}/budgets/${budgetId}`, opts);
      return res.data;
    } catch (err) {
      toast.error("No se pudo cargar el presupuesto vinculado");
      return null;
    }
  };

  const handleVer = async (task) => {
    const b = await fetchBudget(task.budget_id);
    if (b) setViewBudget(b);
  };

  const handleAdjuntos = async (task) => {
    const b = await fetchBudget(task.budget_id);
    if (b) setAttachBudget(b);
  };

  const handleCrearComputo = async (task) => {
    const b = await fetchBudget(task.budget_id);
    if (b) setFormBudget(b);
  };

  const handleEnviarAPresupuestos = async (task) => {
    if (!confirm(`¿Enviar el cómputo a Presupuestos?\n\nEsto marcará la tarea como completada y notificará al área de presupuestos para "Pasar a Cotización".`)) return;
    setEnviandoTaskId(task._id);
    try {
      await axios.post(`${API}/computo-tasks/${task._id}/enviar-a-presupuestos`, {}, opts);
      toast.success("Cómputo enviado a Presupuestos — el área verá el badge 'CÓMPUTO DISPONIBLE'");
      fetchTasks();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al enviar a presupuestos");
    } finally {
      setEnviandoTaskId(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteTask) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/computo-tasks/${deleteTask._id}`, opts);
      toast.success(`${meta.title === "Proyectos" ? "Proyecto" : "Cómputo"} enviado a la papelera`);
      setDeleteTask(null);
      fetchTasks();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al eliminar");
    } finally {
      setDeleting(false);
    }
  };

  const openTerminar = (task) => {
    setTerminarDestinos({ computo: false, presupuesto: false });
    setTerminarTask(task);
  };

  const handleTerminar = async () => {
    if (!terminarTask) return;
    const destinos = Object.entries(terminarDestinos).filter(([, v]) => v).map(([k]) => k);
    if (destinos.length === 0) { toast.error("Elegí al menos un destino"); return; }
    setTerminando(true);
    try {
      const { data } = await axios.post(`${API}/computo-tasks/${terminarTask._id}/terminar`, { destinos }, opts);
      const parts = [];
      if (data.computo_task_id) parts.push("Cómputo");
      if (data.budget_id) parts.push("Presupuesto");
      toast.success(`Proyecto terminado · Enviado a: ${parts.join(" + ") || "—"}`);
      setTerminarTask(null);
      fetchTasks();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al terminar el proyecto");
    } finally {
      setTerminando(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs font-bold text-violet-600 bg-violet-50 px-2 py-0.5 rounded-full uppercase tracking-wide">Ingeniería</span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight" data-testid="computo-page-title">{meta.title}</h1>
          <p className="text-sm text-gray-500 mt-0.5">{meta.subtitle}</p>
        </div>
        <button
          onClick={() => setShowPapelera(true)}
          className="flex items-center gap-2 border border-gray-200 text-gray-600 hover:bg-gray-50 px-3 py-2 rounded-xl text-sm font-semibold transition-colors"
          data-testid="computo-papelera-btn"
        >
          <Archive size={15} /> Papelera
        </button>
      </div>

      {/* Top 5 próximos a vencer */}
      {upcomingTasks.length > 0 && (
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 border-l-4 border-amber-500 rounded-2xl p-4 shadow-sm" data-testid="upcoming-computos-alert">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-9 h-9 rounded-xl bg-amber-200 flex items-center justify-center flex-shrink-0">
              <AlertTriangle size={18} className="text-amber-700" />
            </div>
            <p className="text-xs font-bold text-amber-900 uppercase tracking-wide">
              {meta.upcomingTitle} ({upcomingTasks.length})
            </p>
          </div>
          <div className="space-y-2">
            {upcomingTasks.map((t) => {
              const d = daysUntil(t.deadline_computo);
              const isOverdue = d !== null && d < 0;
              const isToday = d === 0;
              const isUrgent = d !== null && d > 0 && d <= 3;
              let label, labelColor;
              if (d === null) { label = "—"; labelColor = "text-gray-500"; }
              else if (d < 0) { label = `Vencido hace ${Math.abs(d)} día(s)`; labelColor = "text-red-700"; }
              else if (d === 0) { label = "Vence hoy"; labelColor = "text-red-700"; }
              else if (d <= 3) { label = `Vence en ${d} día(s)`; labelColor = "text-orange-600"; }
              else { label = `Vence en ${d} día(s)`; labelColor = "text-gray-500"; }
              return (
                <div
                  key={t._id}
                  className={`flex items-center gap-3 bg-white rounded-xl px-3 py-2 border ${isOverdue || isToday ? "border-red-300" : isUrgent ? "border-orange-200" : "border-gray-100"} shadow-sm`}
                  data-testid={`upcoming-computo-row-${t._id}`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-gray-900 text-sm truncate" title={t.budget_nombre}>{t.budget_nombre}</p>
                    <div className="flex items-center gap-2 text-xs text-gray-600 flex-wrap mt-0.5">
                      <span className="font-mono text-[#1A4B7A] font-bold">{t.budget_number}</span>
                      <span className="text-gray-400">·</span>
                      <span className="truncate">{t.cliente || "—"}</span>
                      <span className="text-gray-400">·</span>
                      <span>{t.deadline_computo}</span>
                    </div>
                  </div>
                  <span className={`text-xs font-bold whitespace-nowrap ${labelColor} ${isToday ? "animate-pulse" : ""}`} data-testid={`upcoming-computo-days-${t._id}`}>
                    {label}
                  </span>
                  <button
                    onClick={() => handleVer(t)}
                    className="bg-amber-600 hover:bg-amber-700 text-white rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors flex-shrink-0"
                    data-testid={`upcoming-computo-view-btn-${t._id}`}
                  >
                    Ver
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { key: "pendiente",  label: "Pendientes",  color: "text-amber-600",  bg: "bg-amber-50",  icon: Clock },
          { key: "en_proceso", label: "En Proceso",  color: "text-blue-600",   bg: "bg-blue-50",   icon: Activity },
          { key: "completado", label: "Completados", color: "text-emerald-600",bg: "bg-emerald-50", icon: CheckSquare },
        ].map(s => (
          <div key={s.key} className={`${s.bg} rounded-xl p-4 border border-gray-100`}>
            <s.icon size={16} className={`${s.color} mb-1.5`} />
            <p className="text-xs text-gray-500 mb-0.5">{s.label}</p>
            <p className={`text-2xl font-bold ${s.color}`}>{stats[s.key]}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        {[["todos", "Todas"], ["pendiente", "Pendientes"], ["en_proceso", "En Proceso"], ["completado", "Completados"]].map(([val, lbl]) => (
          <button
            key={val}
            onClick={() => setFilterStatus(val)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${filterStatus === val ? "bg-[#2E6DA4] text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
            data-testid={`task-filter-${val}`}
          >
            {lbl}
          </button>
        ))}
        <button onClick={fetchTasks} className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors ml-auto">
          <RefreshCw size={14} />
        </button>
      </div>

      {/* Task list */}
      {loading ? (
        <div className="space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-28 rounded-xl" />)}</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 p-16 text-center">
          <ListChecks size={44} className="mx-auto text-gray-200 mb-4" />
          <p className="text-gray-500 font-medium">{meta.emptyTitle}</p>
          <p className="text-sm text-gray-400 mt-1">Las tareas se generan al hacer clic en <strong>"Iniciar Cómputo"</strong> desde un presupuesto en estado <em>Recepción</em></p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="tasks-list">
          {filtered.map(task => {
            const days = daysUntil(task.deadline_computo);
            const isOverdue = days !== null && days < 0;
            const isUrgent = days !== null && days >= 0 && days <= 3;
            const isCompletado = task.status === "completado";
            const yaEnviado = task.enviado_a_presupuestos === true;
            return (
              <div
                key={task._id}
                className={`bg-white rounded-2xl border shadow-sm p-5 transition-all hover:shadow-md ${isOverdue ? "border-red-200" : isUrgent ? "border-amber-200" : "border-gray-100"}`}
                data-testid="task-card"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-2">
                      <span className="font-mono text-xs text-[#1A4B7A] bg-[#EBF3FB] px-2 py-0.5 rounded font-bold">
                        {task.budget_number}
                      </span>
                      {task.status === "terminado" ? (
                        <span className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold border ${TERMINADO_META.color}`} data-testid={`task-terminado-badge-${task._id}`}>
                          <CheckCheck size={11} /> {TERMINADO_META.label}
                        </span>
                      ) : (
                        <TaskStatusSelect task={task} onUpdate={fetchTasks} />
                      )}
                      {yaEnviado && (
                        <span className="flex items-center gap-1 text-xs font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200">
                          <CheckSquare size={10} /> Enviado a Presupuestos
                        </span>
                      )}
                      {isOverdue && !isCompletado && (
                        <span className="flex items-center gap-1 text-xs font-semibold text-red-600 bg-red-50 px-2 py-0.5 rounded-full">
                          <AlertCircle size={10} /> Vencido
                        </span>
                      )}
                      {isUrgent && !isOverdue && !isCompletado && (
                        <span className="flex items-center gap-1 text-xs font-semibold text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">
                          <AlertTriangle size={10} /> {days === 0 ? "Vence hoy" : `${days} día${days !== 1 ? "s" : ""}`}
                        </span>
                      )}
                    </div>
                    <h3 className="font-bold text-gray-900 text-sm mb-1 truncate" title={task.budget_nombre}>
                      {task.budget_nombre}
                    </h3>
                    <p className="text-xs text-gray-500 flex items-center gap-1 mb-2">
                      <Building2 size={11} /> {task.cliente}
                    </p>
                    {task.alcance && (
                      <div className="bg-gray-50 rounded-lg px-3 py-2 border border-gray-100">
                        <p className="text-xs font-semibold text-gray-500 mb-0.5">Alcance:</p>
                        <p className="text-xs text-gray-700 line-clamp-2">{task.alcance}</p>
                      </div>
                    )}
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className={`flex items-center gap-1 text-xs font-semibold ${isOverdue && !isCompletado ? "text-red-600" : isUrgent && !isCompletado ? "text-amber-600" : "text-gray-500"}`}>
                      <CalendarDays size={12} />
                      {task.deadline_computo ? formatDateShort(task.deadline_computo) : "—"}
                    </div>
                    <p className="text-[10px] text-gray-400 mt-0.5">
                      {days !== null && !isCompletado
                        ? isOverdue ? `${Math.abs(days)} d. vencido` : days === 0 ? "Vence hoy" : `${days} días restantes`
                        : ""}
                    </p>
                  </div>
                </div>
                {task.observaciones && (
                  <p className="text-xs text-gray-500 mt-2 italic border-t border-gray-50 pt-2">{task.observaciones}</p>
                )}

                {/* Action buttons (PDF p.1: VER, ADJUNTOS, EDITAR, CREAR CÓMPUTO + ENVIAR cuando completado) */}
                <div className="flex items-center gap-1.5 mt-3 pt-3 border-t border-gray-50 flex-wrap">
                  <button
                    onClick={() => handleVer(task)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#2E6DA4]/10 hover:bg-[#2E6DA4]/20 text-[#2E6DA4] text-xs font-semibold transition-colors"
                    data-testid={`task-ver-btn-${task._id}`}
                  >
                    <Eye size={13} /> Ver
                  </button>
                  <button
                    onClick={() => handleAdjuntos(task)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-purple-50 hover:bg-purple-100 text-purple-700 text-xs font-semibold transition-colors"
                    data-testid={`task-adjuntos-btn-${task._id}`}
                  >
                    <Paperclip size={13} /> Adjuntos
                  </button>
                  <button
                    onClick={() => setEditTask(task)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-semibold transition-colors"
                    data-testid={`task-editar-btn-${task._id}`}
                  >
                    <Edit3 size={13} /> Editar
                  </button>
                  <button
                    onClick={() => handleCrearComputo(task)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-violet-50 hover:bg-violet-100 text-violet-700 text-xs font-semibold transition-colors"
                    data-testid={`task-crear-computo-btn-${task._id}`}
                  >
                    <FilePlus2 size={13} /> Crear Cómputo
                  </button>
                  {/* Fix CL (2026-09-13): cargar los planos y generar el cómputo métrico. */}
                  <button
                    onClick={() => setPlanosTask(task)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-sky-50 hover:bg-sky-100 text-sky-700 text-xs font-semibold transition-colors"
                    data-testid={`task-planos-btn-${task._id}`}
                  >
                    <Ruler size={13} /> Planos y Cómputo
                  </button>
                  {isProyectoModule && task.status !== "terminado" && (
                    <button
                      onClick={() => openTerminar(task)}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-xs font-semibold transition-colors"
                      data-testid={`task-terminar-btn-${task._id}`}
                    >
                      <Flag size={13} /> Marcar TERMINADO
                    </button>
                  )}
                  {isCompletado && !yaEnviado && (
                    <button
                      onClick={() => handleEnviarAPresupuestos(task)}
                      disabled={enviandoTaskId === task._id}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold transition-colors ml-auto disabled:opacity-60"
                      data-testid={`task-enviar-btn-${task._id}`}
                    >
                      {enviandoTaskId === task._id ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
                      Enviar a Presupuestos
                    </button>
                  )}
                  <button
                    onClick={() => setDeleteTask(task)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-50 hover:bg-red-100 text-red-600 text-xs font-semibold transition-colors ${(isCompletado && !yaEnviado) ? "" : "ml-auto"}`}
                    data-testid={`task-eliminar-btn-${task._id}`}
                  >
                    <Trash2 size={13} /> Eliminar
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modals */}
      <Modal open={!!viewBudget} onClose={() => setViewBudget(null)} title={`Presupuesto — ${viewBudget?.nombre || ""}`} fullscreen>
        {viewBudget && <BudgetDetail budget={viewBudget} />}
      </Modal>

      <Modal open={!!formBudget} onClose={() => setFormBudget(null)}
        title={`Cómputo: ${formBudget?.nombre || ""}`} fullscreen>
        {formBudget && (
          <BudgetForm
            budget={formBudget}
            parentBudget={null}
            projects={projects}
            clientes={clientes}
            onSave={() => { setFormBudget(null); fetchTasks(); }}
            onClose={() => setFormBudget(null)}
          />
        )}
      </Modal>

      <AttachmentsModal open={!!attachBudget} onClose={() => setAttachBudget(null)} budget={attachBudget} onUpdated={fetchTasks} />

      {/* Fix CL (2026-09-13): planos del proyecto → cómputo métrico → presupuesto. */}
      <PlanosComputoModal open={!!planosTask} onClose={() => setPlanosTask(null)} task={planosTask} onSaved={fetchTasks} />

      {/* Fix CM (2026-09-13): tareas del flujo de trabajo del Depto. de Ingeniería. */}
      <TareasPanel modulo="ingenieria" rolAsignado="Proyectista,Ingeniería"
        titulo="Tareas y Flujo de Trabajo — Proyectista" />

      <EditTaskModal
        open={!!editTask}
        onClose={() => setEditTask(null)}
        task={editTask}
        onSaved={fetchTasks}
      />

      {/* Confirmación de eliminación (→ papelera) */}
      {deleteTask && createPortal(
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="computo-delete-modal">
          <div className="absolute inset-0 bg-black/60" onClick={() => !deleting && setDeleteTask(null)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                <Trash2 size={20} className="text-red-600" />
              </div>
              <div className="flex-1">
                <h4 className="text-base font-bold text-gray-900">¿Eliminar este {meta.singular}?</h4>
                <p className="text-sm text-gray-600 mt-1.5">
                  <span className="font-semibold">"{deleteTask.budget_nombre}"</span> se moverá a la Papelera por 30 días antes de borrarse definitivamente.
                </p>
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setDeleteTask(null)} disabled={deleting}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm font-medium hover:bg-gray-50 disabled:opacity-60"
                data-testid="computo-delete-cancel-btn"
              >
                Cancelar
              </button>
              <button
                onClick={handleDelete} disabled={deleting}
                className="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-bold inline-flex items-center gap-1.5 disabled:opacity-60"
                data-testid="computo-delete-confirm-btn"
              >
                {deleting ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
                Sí, mover a papelera
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      <ComputoPapeleraModal
        open={showPapelera}
        onClose={() => setShowPapelera(false)}
        onChanged={fetchTasks}
        tipo={tipo}
        title={meta.papeleraTitle}
      />

      {/* PASO C — Modal "Enviar a..." (Cómputo / Presupuesto) */}
      {terminarTask && createPortal(
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="terminar-modal">
          <div className="absolute inset-0 bg-black/60" onClick={() => !terminando && setTerminarTask(null)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <div className="flex items-start gap-3 mb-1">
              <div className="w-10 h-10 rounded-full bg-violet-100 flex items-center justify-center flex-shrink-0">
                <Flag size={20} className="text-violet-600" />
              </div>
              <div className="flex-1">
                <h4 className="text-base font-bold text-gray-900">Enviar a…</h4>
                <p className="text-sm text-gray-600 mt-1">
                  Marcá <span className="font-semibold">"{terminarTask.budget_nombre}"</span> como terminado y elegí a dónde enviarlo (uno o ambos).
                </p>
              </div>
            </div>
            <div className="space-y-2 mt-4">
              <label className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${terminarDestinos.computo ? "border-violet-400 bg-violet-50" : "border-gray-200 hover:bg-gray-50"}`} data-testid="terminar-destino-computo">
                <input
                  type="checkbox"
                  checked={terminarDestinos.computo}
                  onChange={(e) => setTerminarDestinos(d => ({ ...d, computo: e.target.checked }))}
                  className="w-4 h-4 accent-violet-600"
                />
                <Calculator size={16} className="text-violet-600" />
                <div>
                  <p className="text-sm font-semibold text-gray-800">Cómputo</p>
                  <p className="text-xs text-gray-500">Genera el registro en el módulo Cómputo.</p>
                </div>
              </label>
              <label className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${terminarDestinos.presupuesto ? "border-violet-400 bg-violet-50" : "border-gray-200 hover:bg-gray-50"}`} data-testid="terminar-destino-presupuesto">
                <input
                  type="checkbox"
                  checked={terminarDestinos.presupuesto}
                  onChange={(e) => setTerminarDestinos(d => ({ ...d, presupuesto: e.target.checked }))}
                  className="w-4 h-4 accent-violet-600"
                />
                <FileText size={16} className="text-violet-600" />
                <div>
                  <p className="text-sm font-semibold text-gray-800">Presupuesto</p>
                  <p className="text-xs text-gray-500">Reabre el presupuesto original (vuelve a editable).</p>
                </div>
              </label>
            </div>
            <div className="flex gap-2 justify-end mt-5">
              <button
                onClick={() => setTerminarTask(null)} disabled={terminando}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm font-medium hover:bg-gray-50 disabled:opacity-60"
                data-testid="terminar-cancel-btn"
              >
                Cancelar
              </button>
              <button
                onClick={handleTerminar}
                disabled={terminando || (!terminarDestinos.computo && !terminarDestinos.presupuesto)}
                className="px-4 py-2 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-sm font-bold inline-flex items-center gap-1.5 disabled:opacity-50"
                data-testid="terminar-confirm-btn"
              >
                {terminando ? <Loader2 size={13} className="animate-spin" /> : <Flag size={13} />}
                Enviar y terminar
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

export default function Projects() {
  return <ComputoTasksPage tipo="computo" />;
}
